/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { getAnalysisJobStatus, getAnalysisJobResults, saveAnalysisJobAsTemplate } from '../api/api';
import { AnalysisJobStatus, AnalysisJobStatusDisplay } from '../utils/analysisConstants';
import HistogramChart from '../components/analysis/HistogramChart';
import { formatStoredDateTime } from '../utils/datetime';

const formatMetadataValue = (value, fallback = 'Not provided') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  if (typeof value === 'string') {
    return value;
  }

  return String(value);
};

const prettifyResultKey = (value) => value
  .split('_')
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

const formatResultValue = (value) => {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value.toFixed(2) : String(value);
  }

  if (typeof value === 'string' || typeof value === 'boolean') {
    return String(value);
  }

  return JSON.stringify(value);
};

const coercePresentationNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const normalizedValue = value.trim();
  if (!normalizedValue) {
    return null;
  }

  const numericMatch = normalizedValue.match(/^[-+]?\d+(?:\.\d+)?%?$/);
  if (!numericMatch) {
    return null;
  }

  const numericValue = Number.parseFloat(normalizedValue.replace('%', ''));
  return Number.isFinite(numericValue) ? numericValue : null;
};

const getResultPresentationPrecision = (presentation, fallback) => {
  if (Number.isInteger(presentation?.precision) && presentation.precision >= 0) {
    return presentation.precision;
  }

  return fallback;
};

const getResultPresentationAlignmentClass = (presentation) => {
  switch (presentation?.align) {
    case 'center':
      return 'text-center';
    case 'right':
      return 'text-right';
    default:
      return 'text-left';
  }
};

const formatResultValueWithPresentation = (value, presentation) => {
  if (!presentation || typeof presentation !== 'object') {
    return formatResultValue(value);
  }

  const numericValue = coercePresentationNumber(value);
  let formattedValue = null;

  switch (presentation.format) {
    case 'percentage':
      if (numericValue !== null) {
        formattedValue = `${numericValue.toFixed(getResultPresentationPrecision(presentation, 2))}%`;
      }
      break;
    case 'integer':
      if (numericValue !== null) {
        formattedValue = Math.round(numericValue).toLocaleString();
      }
      break;
    case 'decimal':
      if (numericValue !== null) {
        formattedValue = numericValue.toFixed(getResultPresentationPrecision(presentation, 2));
      }
      break;
    default:
      break;
  }

  if (formattedValue === null) {
    formattedValue = formatResultValue(value);
  }

  if (typeof presentation.prefix === 'string' && presentation.prefix) {
    formattedValue = `${presentation.prefix}${formattedValue}`;
  }
  if (typeof presentation.suffix === 'string' && presentation.suffix) {
    formattedValue = `${formattedValue}${presentation.suffix}`;
  }

  return formattedValue;
};

const RESULT_VERDICT_BADGE_STYLES = {
  supported: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  contradicted: 'border-rose-200 bg-rose-50 text-rose-700',
  conditional: 'border-sky-200 bg-sky-50 text-sky-700',
  inconclusive: 'border-slate-200 bg-slate-50 text-slate-700',
};

const RESULT_WARNING_STYLES = {
  error: 'border-rose-200 bg-rose-50 text-rose-700',
  warning: 'border-amber-200 bg-amber-50 text-amber-700',
  info: 'border-sky-200 bg-sky-50 text-sky-700',
};

const getResultVerdictBadgeClasses = (status) => RESULT_VERDICT_BADGE_STYLES[status] || RESULT_VERDICT_BADGE_STYLES.inconclusive;
const getResultWarningClasses = (severity) => RESULT_WARNING_STYLES[severity] || RESULT_WARNING_STYLES.warning;
const getRecoveryActionButtonClasses = (actionType) => (
  actionType === 'retry_same_spec'
    ? 'border-transparent bg-primary text-white hover:bg-primary-dark'
    : 'border-borderDefault bg-backgroundSurface text-textPrimary hover:bg-backgroundBody'
);

const buildDefaultTemplateName = (jobStatus) => {
  if (!jobStatus || typeof jobStatus !== 'object') {
    return '';
  }

  if (typeof jobStatus.name === 'string' && jobStatus.name.trim()) {
    return jobStatus.name.trim();
  }

  const analysisType = typeof jobStatus.analysis_type === 'string'
    ? jobStatus.analysis_type
      .split('_')
      .filter(Boolean)
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ')
    : 'Analysis';

  return [analysisType, jobStatus.instrument_symbol, jobStatus.target_timeframe]
    .filter((value) => typeof value === 'string' && value.trim())
    .join(' ');
};

const StructuredWarningList = ({ title, warnings }) => {
  if (!Array.isArray(warnings) || warnings.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 space-y-3">
      <h3 className="text-sm font-semibold text-textPrimary">{title}</h3>
      {warnings.map((warning) => (
        <div key={warning.message} className={`rounded border p-3 ${getResultWarningClasses(warning.severity)}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide">{formatMetadataValue(warning.category, 'warning')}</span>
            <span className="text-xs font-medium opacity-80">{formatMetadataValue(warning.severity, 'warning')}</span>
          </div>
          <p className="mt-2 text-sm">{warning.message}</p>
        </div>
      ))}
    </div>
  );
};

const getResultSchemaComponents = (resultContract) => {
  const schemaComponents = resultContract?.schema?.components;
  return Array.isArray(schemaComponents) ? schemaComponents : [];
};

const getSchemaComponentPresentation = (schemaComponent) => {
  if (!schemaComponent || typeof schemaComponent.presentation !== 'object' || Array.isArray(schemaComponent.presentation)) {
    return null;
  }

  return schemaComponent.presentation;
};

const normalizeComponentTitle = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim().toLowerCase();
};

const getSchemaTitleMatchers = (schemaComponent) => {
  if (!schemaComponent || typeof schemaComponent !== 'object') {
    return [];
  }

  return [
    schemaComponent.title ? { mode: 'exact', value: schemaComponent.title } : null,
    schemaComponent.title_contains ? { mode: 'contains', value: schemaComponent.title_contains } : null,
    schemaComponent.title_starts_with ? { mode: 'starts_with', value: schemaComponent.title_starts_with } : null,
    schemaComponent.title_ends_with ? { mode: 'ends_with', value: schemaComponent.title_ends_with } : null,
  ].filter(Boolean);
};

const getSchemaComponentMatchScore = (component, schemaComponent) => {
  if (!component || component.type !== schemaComponent?.type) {
    return -1;
  }

  const titleMatchers = getSchemaTitleMatchers(schemaComponent);
  if (titleMatchers.length === 0) {
    return 1;
  }

  const componentTitle = normalizeComponentTitle(component.title);
  let score = 1;

  titleMatchers.forEach((matcher) => {
    const matcherValue = normalizeComponentTitle(matcher.value);
    if (!matcherValue) {
      return;
    }

    if (matcher.mode === 'exact' && componentTitle === matcherValue) {
      score = Math.max(score, 8);
      return;
    }
    if (matcher.mode === 'contains' && componentTitle.includes(matcherValue)) {
      score = Math.max(score, 6);
      return;
    }
    if (matcher.mode === 'starts_with' && componentTitle.startsWith(matcherValue)) {
      score = Math.max(score, 5);
      return;
    }
    if (matcher.mode === 'ends_with' && componentTitle.endsWith(matcherValue)) {
      score = Math.max(score, 5);
    }
  });

  return score;
};

// Placeholder for result renderers - these will be separate components
const ResultTable = ({ title, data, schemaComponent }) => {
  const presentation = getSchemaComponentPresentation(schemaComponent);
  const columnPresentations = presentation?.columns && typeof presentation.columns === 'object'
    ? presentation.columns
    : {};

  return (
    <div className="mt-4 p-3 bg-backgroundBody rounded shadow">
      <h3 className="text-lg font-semibold mb-2 text-textPrimary">{title}</h3>
      {data && data.columns && data.rows ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-borderDefault">
            <thead className="bg-backgroundOffset">
              <tr>
                {data.columns.map((col) => {
                  const columnPresentation = columnPresentations[col.key];

                  return (
                    <th
                      key={col.key}
                      scope="col"
                      className={`px-4 py-2 text-xs font-medium text-textSecondary uppercase tracking-wider ${getResultPresentationAlignmentClass(columnPresentation)}`}
                    >
                      {col.label}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-backgroundSurface divide-y divide-borderDefault">
              {data.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {data.columns.map((col) => {
                    const columnPresentation = columnPresentations[col.key];

                    return (
                      <td
                        key={col.key}
                        className={`px-4 py-2 whitespace-nowrap text-sm text-textPrimary ${getResultPresentationAlignmentClass(columnPresentation)}`}
                      >
                        {formatResultValueWithPresentation(row[col.key], columnPresentation)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="text-textSecondary">Table data is not available or malformed.</p>}
    </div>
  );
};

const ResultKeyValuePairs = ({ title, data, schemaComponent }) => {
  const presentation = getSchemaComponentPresentation(schemaComponent);
  const metricPresentations = presentation?.metrics && typeof presentation.metrics === 'object'
    ? presentation.metrics
    : {};
  const highlightedKeys = new Set(Array.isArray(presentation?.highlight_keys) ? presentation.highlight_keys : []);

  return (
    <div className="mt-4 p-3 bg-backgroundBody rounded shadow">
      <h3 className="text-lg font-semibold mb-2 text-textPrimary">{title}</h3>
      {data && Array.isArray(data) ? (
        <ul className="space-y-1">
          {data.map((pair) => {
            const metricPresentation = metricPresentations[pair.key];
            const isHighlighted = highlightedKeys.has(pair.key);

            return (
              <li key={pair.key} className="text-sm">
                <span className="font-medium text-textSecondary">{pair.key}: </span>
                <span className={isHighlighted ? 'font-semibold text-textPrimary' : 'text-textPrimary'}>
                  {formatResultValueWithPresentation(pair.value, metricPresentation)}
                </span>
              </li>
            );
          })}
        </ul>
      ) : <p className="text-textSecondary">Key-value data is not available or malformed.</p>}
    </div>
  );
};

const ResultImageSVG = ({ title, data }) => (
  <div className="mt-4 p-3 bg-backgroundBody rounded shadow">
    <h3 className="text-lg font-semibold mb-2 text-textPrimary">{title}</h3>
    {data && data.svg_content ? (
      <div className="overflow-x-auto" dangerouslySetInnerHTML={{ __html: data.svg_content }} />
    ) : <p className="text-textSecondary">SVG image is not available or malformed.</p>}
  </div>
);

const ResultMarkdownText = ({ title, data, schemaComponent }) => {
  const markdownText = typeof data === 'string' ? data : data?.text;
  const isErrorState = schemaComponent?.state === 'error';

  return (
    <div className={`mt-4 p-3 rounded shadow ${isErrorState ? 'border border-rose-200 bg-rose-50' : 'bg-backgroundBody'}`}>
      {title ? <h3 className="text-lg font-semibold mb-2 text-textPrimary">{title}</h3> : null}
      {markdownText ? (
        <div className={`text-sm whitespace-pre-wrap break-words ${isErrorState ? 'text-rose-700' : 'text-textPrimary'}`}>{markdownText}</div>
      ) : <p className="text-textSecondary">Markdown text is not available or malformed.</p>}
    </div>
  );
};

const RESULT_COMPONENT_RENDERERS = {
  table: ResultTable,
  key_value_pairs: ResultKeyValuePairs,
  image_svg: ResultImageSVG,
  histogram: HistogramChart,
  markdown_text: ResultMarkdownText,
};

const buildFlatResultFallbackComponents = (resultData, resultContract) => {
  if (!resultData || typeof resultData !== 'object' || Array.isArray(resultData)) {
    return [];
  }

  const fallbackEntries = Object.entries(resultData)
    .filter(([key]) => key !== 'analysis_display_name' && key !== 'summary')
    .map(([key, value]) => ({
      key: prettifyResultKey(key),
      value: formatResultValue(value),
    }));

  if (fallbackEntries.length === 0) {
    return [];
  }

  const keyValueSchema = getResultSchemaComponents(resultContract)
    .find((component) => component.type === 'key_value_pairs');

  return [
    {
      type: 'key_value_pairs',
      title: resultData.analysis_display_name || keyValueSchema?.title || 'Analysis Data',
      data: fallbackEntries,
    },
  ];
};

const resolveResultPresentation = (resultData, resultContract) => {
  const actualComponents = Array.isArray(resultData?.components)
    ? resultData.components.filter((component) => component && typeof component === 'object')
    : buildFlatResultFallbackComponents(resultData, resultContract);
  const schemaComponents = getResultSchemaComponents(resultContract);

  if (schemaComponents.length === 0) {
    return {
      components: actualComponents.map((component, index) => ({
        ...component,
        renderKey: `component-${index}`,
      })),
      notices: [],
    };
  }

  const usedIndices = new Set();
  const resolvedComponents = [];
  const notices = [];

  schemaComponents.forEach((schemaComponent, schemaIndex) => {
    const matches = actualComponents
      .map((component, index) => ({ component, index }))
      .filter(({ index }) => !usedIndices.has(index))
      .map(({ component, index }) => ({
        component,
        index,
        score: getSchemaComponentMatchScore(component, schemaComponent),
      }))
      .filter(({ score }) => score >= 0)
      .sort((left, right) => right.score - left.score || left.index - right.index);

    if (schemaComponent.repeatable) {
      if (matches.length === 0 && schemaComponent.required) {
        notices.push(`Required result component missing: ${schemaComponent.title || schemaComponent.type}.`);
        return;
      }

      matches.forEach(({ component, index }) => {
        usedIndices.add(index);
        resolvedComponents.push({
          ...component,
          title: component.title || schemaComponent.title,
          schemaComponent,
          renderKey: `schema-${schemaIndex}-${index}`,
        });
      });
      return;
    }

    const firstMatch = matches[0];
    if (!firstMatch) {
      if (schemaComponent.required) {
        notices.push(`Required result component missing: ${schemaComponent.title || schemaComponent.type}.`);
      }
      return;
    }

    usedIndices.add(firstMatch.index);
    resolvedComponents.push({
      ...firstMatch.component,
      title: firstMatch.component.title || schemaComponent.title,
      schemaComponent,
      renderKey: `schema-${schemaIndex}-${firstMatch.index}`,
    });
  });

  actualComponents.forEach((component, index) => {
    if (usedIndices.has(index)) {
      return;
    }

    resolvedComponents.push({
      ...component,
      schemaComponent: null,
      renderKey: `extra-${index}`,
    });
  });

  return { components: resolvedComponents, notices };
};

const renderResultComponent = (component) => {
  const Renderer = RESULT_COMPONENT_RENDERERS[component.type];
  if (!Renderer) {
    return (
      <p key={component.renderKey} className="mt-4 text-textSecondary">
        Unsupported result component type: {component.type}
      </p>
    );
  }

  return <Renderer key={component.renderKey} title={component.title} data={component.data} schemaComponent={component.schemaComponent} />;
};

const AnalysisResultRenderer = ({ resultData, resultContract, resultWarnings }) => {
  if (!resultData) {
    return <p className="text-textSecondary">No result data available.</p>;
  }

  const { components, notices } = resolveResultPresentation(resultData, resultContract);
  const effectiveWarnings = [];
  const seenWarningMessages = new Set();

  if (Array.isArray(resultWarnings)) {
    resultWarnings.forEach((warning) => {
      if (!warning || typeof warning !== 'object' || typeof warning.message !== 'string') {
        return;
      }
      if (seenWarningMessages.has(warning.message)) {
        return;
      }
      effectiveWarnings.push(warning);
      seenWarningMessages.add(warning.message);
    });
  }

  notices.forEach((notice) => {
    if (seenWarningMessages.has(notice)) {
      return;
    }
    effectiveWarnings.push({
      code: 'result_contract_missing_component',
      severity: 'warning',
      category: 'contract',
      message: notice,
    });
    seenWarningMessages.add(notice);
  });

  if (components.length === 0 && effectiveWarnings.length === 0) {
    return <p className="text-textSecondary">Result data is present but could not be displayed in a recognized format.</p>;
  }

  return (
    <div>
      <StructuredWarningList title="Result Warnings" warnings={effectiveWarnings} />
      {components.map(renderResultComponent)}
    </div>
  );
};


const JOB_DETAILS_POLL_INTERVAL = 3000; // Poll more frequently for an active job view

const AnalysisJobDetailsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [jobStatus, setJobStatus] = useState(null);
  const [jobResults, setJobResults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [templateName, setTemplateName] = useState('');
  // The suggested name may only pre-fill the field until the user interacts with it; once
  // they have (even if they cleared it), a later jobStatus refresh must never stomp it.
  const templateNameTouched = useRef(false);
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateChangelogNote, setTemplateChangelogNote] = useState('');
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [templateSaveError, setTemplateSaveError] = useState(null);
  const [templateSaveSuccess, setTemplateSaveSuccess] = useState(null);

  const fetchJobData = useCallback(async () => {
    try {
      setIsLoading(true);
      const statusData = await getAnalysisJobStatus(jobId);
      setJobStatus(statusData);

      if (statusData.status === AnalysisJobStatus.COMPLETED_SUCCESS) {
        const resultsData = await getAnalysisJobResults(jobId);
        setJobResults(resultsData);
      }
      setError(null);
    } catch (err) {
      console.error("Failed to fetch job details:", err);
      setError(err.detail || err.message || "Failed to load job details.");
      // Keep existing data if partial load, or clear if total failure
      // setJobStatus(null); 
      // setJobResults(null);
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  // Effect for initial fetch when component mounts or jobId changes
  useEffect(() => {
    fetchJobData();
  }, [jobId, fetchJobData]); // fetchJobData is stable due to useCallback

  useEffect(() => {
    if (!jobStatus || jobStatus.status !== AnalysisJobStatus.COMPLETED_SUCCESS) {
      return;
    }

    setTemplateName((currentValue) =>
      templateNameTouched.current ? currentValue : (currentValue || buildDefaultTemplateName(jobStatus))
    );
  }, [jobStatus]);

  // Determine if the job is active (i.e., not completed or failed)
  const isJobActive = jobStatus &&
                      jobStatus.status !== AnalysisJobStatus.COMPLETED_SUCCESS &&
                      jobStatus.status !== AnalysisJobStatus.COMPLETED_FAIL;
  const planningProvenance = jobResults?.planning_provenance || jobStatus?.planning_provenance || {};
  const resultContract = jobResults?.result_contract;
  const resultOverview = jobResults?.result_overview;
  const resultVerdict = jobResults?.result_verdict;
  const resultWarnings = jobResults?.result_warnings;
  const recoveryActions = jobResults?.recovery_actions || jobStatus?.recovery_actions || [];
  const statusWarnings = jobStatus?.status_warnings;
  const hasUnifiedResultWarnings = Array.isArray(resultWarnings) && resultWarnings.length > 0;
  const hasStatusWarnings = Array.isArray(statusWarnings) && statusWarnings.length > 0;
  const executedSpec = jobResults?.executed_spec;

  const handleRecoveryAction = useCallback((action) => {
    if (!action?.draft) {
      return;
    }

    navigate(action.route || '/analysis', {
      state: {
        analysisRecoveryDraft: action.draft,
      },
    });
  }, [navigate]);

  const handleSaveTemplate = useCallback(async () => {
    const normalizedName = templateName.trim();
    if (!normalizedName) {
      setTemplateSaveError('Template name is required.');
      setTemplateSaveSuccess(null);
      return;
    }

    setIsSavingTemplate(true);
    setTemplateSaveError(null);
    setTemplateSaveSuccess(null);
    try {
      const response = await saveAnalysisJobAsTemplate(jobId, {
        name: normalizedName,
        description: templateDescription.trim(),
        changelog_note: templateChangelogNote.trim(),
      });
      const savedVersionNumber = response?.saved_version?.version_number;
      setTemplateName(response?.template?.name || normalizedName);
      setTemplateDescription(response?.template?.description || templateDescription.trim());
      setTemplateChangelogNote('');
      setTemplateSaveSuccess(
        savedVersionNumber
          ? `Saved as template version ${savedVersionNumber}.`
          : 'Saved as template.'
      );
    } catch (err) {
      setTemplateSaveError(err?.detail || err?.message || 'Saving template failed.');
    } finally {
      setIsSavingTemplate(false);
    }
  }, [jobId, templateChangelogNote, templateDescription, templateName]);

  // Effect for managing the polling interval
  useEffect(() => {
    let intervalId = null;
    if (isJobActive) {
      // Start polling if the job is active
      intervalId = setInterval(fetchJobData, JOB_DETAILS_POLL_INTERVAL);
    }
    
    // Cleanup function to clear the interval when the component unmounts
    // or when isJobActive or fetchJobData changes (though fetchJobData should be stable)
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isJobActive, fetchJobData]); // Dependencies: isJobActive (boolean) and fetchJobData (stable callback)


  if (isLoading && !jobStatus) { // Show loading only on initial load
    return <PageLayout title="Loading Job Details..."><p className="text-center text-textSecondary">Fetching data...</p></PageLayout>;
  }

  if (error && !jobStatus) { // Show error if initial load failed completely
    return <PageLayout title="Error"><p className="text-red-500 text-center">{error}</p></PageLayout>;
  }
  
  if (!jobStatus) { // Should ideally be covered by loading/error, but as a fallback
    return <PageLayout title="Job Not Found"><p className="text-center text-textSecondary">Could not retrieve job information.</p></PageLayout>;
  }

  return (
    <PageLayout 
      title={`Analysis Job: ${jobStatus.job_id.substring(0,8)}...`}
      backButton={{ label: 'Back to Analysis', fallbackTo: '/analysis' }}
    >
      {error && <p className="mb-4 text-red-500 bg-red-100 p-3 rounded-md">Error updating job: {error}</p>}
      
      <section className="mb-6 p-4 bg-backgroundSurface rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-3 text-textPrimary">Job Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div><strong>Job ID:</strong> {jobStatus.job_id}</div>
          <div><strong>Status:</strong> 
            <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
              jobStatus.status === AnalysisJobStatus.COMPLETED_SUCCESS ? 'bg-green-100 text-green-700' :
              jobStatus.status === AnalysisJobStatus.COMPLETED_FAIL ? 'bg-red-100 text-red-700' :
              jobStatus.status === AnalysisJobStatus.RUNNING_ANALYSIS ? 'bg-blue-100 text-blue-700' :
              'bg-yellow-100 text-yellow-700'
            }`}>
              {AnalysisJobStatusDisplay[jobStatus.status] || jobStatus.status}
            </span>
          </div>
          <div><strong>Analysis Type:</strong> {jobStatus.analysis_type.replace(/_/g, ' ')}</div>
          <div><strong>Instrument:</strong> {jobStatus.instrument_symbol}</div>
          <div><strong>Target Timeframe:</strong> {jobStatus.target_timeframe}</div>
          <div><strong>Definition Source:</strong> {formatMetadataValue(jobStatus.definition_source)}</div>
          <div><strong>Executor Type:</strong> {formatMetadataValue(jobStatus.executor_type)}</div>
          <div><strong>Snapshot Source:</strong> {formatMetadataValue(planningProvenance.snapshot_source)}</div>
          <div><strong>Planner Source:</strong> {formatMetadataValue(planningProvenance.planner_source, 'Manual or direct spec submit')}</div>
          <div><strong>Planner Model:</strong> {formatMetadataValue(planningProvenance.planner_model)}</div>
          <div><strong>Created At:</strong> {formatStoredDateTime(jobStatus.created_at, undefined, '-')}</div>
          <div><strong>Last Updated:</strong> {formatStoredDateTime(jobStatus.updated_at, undefined, '-')}</div>
        </div>
        {jobStatus.status === AnalysisJobStatus.COMPLETED_FAIL && hasStatusWarnings && (
          <StructuredWarningList title="Execution Warnings" warnings={statusWarnings} />
        )}
        {jobStatus.status === AnalysisJobStatus.COMPLETED_FAIL && jobStatus.error_message && !hasStatusWarnings && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
            <h3 className="text-md font-semibold text-red-700">Error Details:</h3>
            <p className="text-sm text-red-600 whitespace-pre-wrap">{jobStatus.error_message}</p>
          </div>
        )}
      </section>

      {Array.isArray(recoveryActions) && recoveryActions.length > 0 && (
        <section className="mb-6 p-4 bg-backgroundSurface rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-3 text-textPrimary">Recovery Actions</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {recoveryActions.map((action) => (
              <div key={action.type} className="rounded-lg border border-borderDefault bg-backgroundBody p-4">
                <h3 className="text-base font-semibold text-textPrimary">{formatMetadataValue(action.label, 'Recovery Action')}</h3>
                {action.description && (
                  <p className="mt-2 text-sm text-textSecondary">{action.description}</p>
                )}
                <button
                  type="button"
                  onClick={() => handleRecoveryAction(action)}
                  className={`mt-3 inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium shadow-sm ${getRecoveryActionButtonClasses(action.type)}`}
                >
                  {formatMetadataValue(action.label, 'Open Recovery Draft')}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {jobStatus.status === AnalysisJobStatus.COMPLETED_SUCCESS && (
        <section className="mb-6 p-4 bg-backgroundSurface rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-3 text-textPrimary">Save As Template</h2>
          <p className="text-sm text-textSecondary">
            Capture this successful run as a reusable template version. Loading a template on the analysis page creates an editable draft before submission.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="templateName" className="block text-sm font-medium text-textSecondary">Template Name</label>
              <input
                id="templateName"
                type="text"
                value={templateName}
                onChange={(event) => { templateNameTouched.current = true; setTemplateName(event.target.value); }}
                className="mt-1 block w-full rounded-md border border-borderDefault bg-backgroundInput p-2 text-textPrimary shadow-sm"
              />
            </div>
            <div>
              <label htmlFor="templateChangelogNote" className="block text-sm font-medium text-textSecondary">Version Note</label>
              <input
                id="templateChangelogNote"
                type="text"
                value={templateChangelogNote}
                onChange={(event) => setTemplateChangelogNote(event.target.value)}
                className="mt-1 block w-full rounded-md border border-borderDefault bg-backgroundInput p-2 text-textPrimary shadow-sm"
                placeholder="What changed in this version?"
              />
            </div>
          </div>
          <div className="mt-4">
            <label htmlFor="templateDescription" className="block text-sm font-medium text-textSecondary">Template Description</label>
            <textarea
              id="templateDescription"
              rows={3}
              value={templateDescription}
              onChange={(event) => setTemplateDescription(event.target.value)}
              className="mt-1 block w-full rounded-md border border-borderDefault bg-backgroundInput p-2 text-textPrimary shadow-sm"
              placeholder="Summarize when this template should be used."
            />
          </div>
          {templateSaveError && (
            <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{templateSaveError}</p>
          )}
          {templateSaveSuccess && (
            <p className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{templateSaveSuccess}</p>
          )}
          <button
            type="button"
            onClick={handleSaveTemplate}
            disabled={isSavingTemplate}
            className="mt-4 inline-flex items-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-dark disabled:opacity-50"
          >
            {isSavingTemplate ? 'Saving Template...' : 'Save As Template'}
          </button>
        </section>
      )}

      {jobStatus.status === AnalysisJobStatus.COMPLETED_SUCCESS && jobResults && (
        <section className="p-4 bg-backgroundSurface rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-3 text-textPrimary">Analysis Results</h2>
          <p className="text-sm text-textSecondary mb-1">Generated At: {formatStoredDateTime(jobResults.generated_at, undefined, '-')}</p>
          {resultOverview && (
            <div className="mt-4 p-4 bg-backgroundBody rounded shadow-sm">
              <h3 className="text-lg font-semibold mb-2 text-textPrimary">Research Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div><strong>Title:</strong> {formatMetadataValue(resultOverview.title)}</div>
                <div><strong>Timeframe:</strong> {formatMetadataValue(resultOverview.timeframe)}</div>
                <div><strong>Date Range:</strong> {formatMetadataValue(resultOverview?.date_range?.start)} to {formatMetadataValue(resultOverview?.date_range?.end)}</div>
                <div><strong>Sample Size:</strong> {formatMetadataValue(resultOverview.sample_size, 'Unknown')}</div>
              </div>
              {resultOverview.summary && (
                <p className="mt-3 text-sm text-textSecondary whitespace-pre-wrap">{resultOverview.summary}</p>
              )}
              {Array.isArray(resultOverview.metric_highlights) && resultOverview.metric_highlights.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {resultOverview.metric_highlights.map((metric) => (
                    <div key={metric.label} className="px-3 py-2 rounded bg-backgroundSurface border border-borderDefault">
                      <div className="text-xs font-medium text-textSecondary">{metric.label}</div>
                      <div className="text-sm font-semibold text-textPrimary">{formatMetadataValue(metric.value)}</div>
                    </div>
                  ))}
                </div>
              )}
              {!hasUnifiedResultWarnings && Array.isArray(resultOverview.warnings) && resultOverview.warnings.length > 0 && (
                <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3">
                  <h4 className="text-sm font-semibold text-amber-700">Quality Warnings</h4>
                  <ul className="mt-2 list-disc list-inside space-y-1 text-sm text-amber-700">
                    {resultOverview.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {resultVerdict && (
            <div className="mt-4 p-4 bg-backgroundBody rounded shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-textPrimary">Deterministic Verdict</h3>
                  {resultVerdict.summary && (
                    <p className="mt-2 text-sm text-textSecondary whitespace-pre-wrap">{resultVerdict.summary}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${getResultVerdictBadgeClasses(resultVerdict.status)}`}>
                    {formatMetadataValue(resultVerdict.label, prettifyResultKey(resultVerdict.status || 'inconclusive'))}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-borderDefault bg-backgroundSurface px-3 py-1 text-xs font-medium text-textSecondary">
                    Confidence: {formatMetadataValue(resultVerdict.confidence, 'Unknown')}
                  </span>
                </div>
              </div>
              {resultVerdict.primary_metric && (
                <div className="mt-3 inline-flex min-w-[12rem] flex-col rounded border border-borderDefault bg-backgroundSurface px-3 py-2">
                  <span className="text-xs font-medium text-textSecondary">{formatMetadataValue(resultVerdict.primary_metric.label)}</span>
                  <span className="text-sm font-semibold text-textPrimary">{formatMetadataValue(resultVerdict.primary_metric.value)}</span>
                </div>
              )}
              {Array.isArray(resultVerdict.supporting_metrics) && resultVerdict.supporting_metrics.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {resultVerdict.supporting_metrics.map((metric) => (
                    <div key={`${metric.label}-${metric.value}`} className="px-3 py-2 rounded bg-backgroundSurface border border-borderDefault">
                      <div className="text-xs font-medium text-textSecondary">{formatMetadataValue(metric.label)}</div>
                      <div className="text-sm font-semibold text-textPrimary">{formatMetadataValue(metric.value)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {resultContract && (
            <div className="mt-4 p-3 bg-backgroundBody rounded shadow-sm">
              <h3 className="text-lg font-semibold mb-2 text-textPrimary">Result Contract</h3>
              {Array.isArray(resultContract.component_types) && resultContract.component_types.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {resultContract.component_types.map((componentType, index) => (
                    <span key={`${componentType}-${index}`} className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                      {componentType}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-textSecondary">No declared result contract is available for this analysis type.</p>
              )}
            </div>
          )}
          <AnalysisResultRenderer resultData={jobResults.result_data} resultContract={jobResults.result_contract} resultWarnings={jobResults.result_warnings} /> 
          {executedSpec && (
            <details className="mt-4 p-3 bg-backgroundBody rounded shadow-sm">
              <summary className="cursor-pointer text-sm font-semibold text-textPrimary">Executed Spec JSON</summary>
              <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-words text-xs text-textPrimary">{JSON.stringify(executedSpec, null, 2)}</pre>
            </details>
          )}
        </section>
      )}

      {isLoading && jobStatus && <p className="text-center text-textSecondary mt-4">Updating job status...</p>}

      {(jobStatus.status !== AnalysisJobStatus.COMPLETED_SUCCESS && jobStatus.status !== AnalysisJobStatus.COMPLETED_FAIL && !jobResults) && (
         <section className="mt-6 p-4 bg-backgroundSurface rounded-lg shadow text-center">
            <h2 className="text-xl font-semibold mb-3 text-textPrimary">Results Pending</h2>
            <p className="text-textSecondary">Analysis is currently in progress. Status: {AnalysisJobStatusDisplay[jobStatus.status] || jobStatus.status}</p>
            <p className="text-textSecondary mt-2">This page will automatically update.</p>
         </section>
      )}
    </PageLayout>
  );
};

export default AnalysisJobDetailsPage;
